// src/store/category.js

import { defineStore } from 'pinia';
import { http } from './https';
import { LOCAL } from '@/constants';
import cache from '@/cache';

export const useHomeStore = defineStore('home', {
    state: () => ({
        categoriesGames: {},
        sliders: [],
        categories: [],
        lang: localStorage.getItem(LOCAL) || "en",
        searchGames: [],
        games: [],
        leaderboard: [],
        newGames: [],
        mostViews: [],
        mostRated: [],
        mostCommented: [],
        theme: 'dark',
        isHomeDataLoaded: false,
    }),
    getters: {
        getCategories: (state) => state.categories,
        getCategoriesGames: (state) => state.categoriesGames,
        getLeaderboard: (state) => state.leaderboard,
    },
    actions: {
        async fetchHomePageData(forceRefresh = false) {
            const cacheKey = `home_${this.lang}`;
            
            // Try to load from cache first (unless force refresh)
            if (!forceRefresh) {
                const cachedData = cache.get(cacheKey);
                if (cachedData) {
                    this.categoriesGames = cachedData.categoriesGames;
                    this.sliders = cachedData.sliders;
                    this.categories = cachedData.categories;
                    this.newGames = cachedData.newGames;
                    this.mostViews = cachedData.mostViews;
                    this.mostRated = cachedData.mostRated;
                    this.mostCommented = cachedData.mostCommented;
                    this.isHomeDataLoaded = true;
                    return;
                }
            }
            
            try {
                const response = await http.get('/home?lang='+this.lang);
                const data = response.data;

                // Filter out categories with no games and then map over the remaining categories
                this.categoriesGames = data.categoriesWithGames
                    .filter(category => category.games && category.games.length > 0)
                    .map(category => ({
                        ...category,
                        games: category.games.slice(0, 6)
                    }));

                this.sliders = data.sliders;
                this.categories = data.categories.filter(category => category.games && category.games.length > 0);
                localStorage.setItem('categories', JSON.stringify(data.categories));
                this.newGames = data.new_games;
                this.mostViews = data.most_views;
                this.mostRated = data.most_rated;
                this.mostCommented = data.most_commented;
                this.isHomeDataLoaded = true;
                
                // Cache the data
                cache.set(cacheKey, {
                    categoriesGames: this.categoriesGames,
                    sliders: this.sliders,
                    categories: this.categories,
                    newGames: this.newGames,
                    mostViews: this.mostViews,
                    mostRated: this.mostRated,
                    mostCommented: this.mostCommented,
                }, 'home');
            } catch (error) {
                console.error(error);
            }
        },
        async fetchLeaderboard() {
            try {
                // Use showLoading: false to prevent triggering the global loading spinner
                // This prevents leaderboard polling from interfering with game loading
                const response = await http.get('/leaderboard', { showLoading: false });
                this.leaderboard = response.data.leaderboard;
            } catch (error) {
                console.error(error);
            }
        },
        async fetchConfigData() {
            const currentTime = new Date().getTime();
        
            const storedConfig = localStorage.getItem('config');
            const storedTimestamp = localStorage.getItem('config_timestamp');
        
            if (storedConfig && storedTimestamp && (currentTime - storedTimestamp) < (1000 * 60 * 60 * 24)) {
                return "config-exist";
            }
        
            try {
                const response = await http.get('/config');
                const data = response.data;
        
                if (data.status) {
                    localStorage.setItem('config', JSON.stringify(data.product));
                    localStorage.setItem('config_timestamp', currentTime.toString());  // Save the current timestamp
                    return "config-loaded";
                }
            } catch (error) {
                console.error(error);
            }
        
            return "config-missing";
        },
        toggleTheme() {
            this.theme = this.theme === 'light' ? 'dark' : 'light';
            document.documentElement.setAttribute('data-theme', this.theme);
            localStorage.setItem('theme', this.theme);
        },
        loadTheme() {
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme) {
              this.theme = savedTheme;
            } else {
                // const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                // this.theme = prefersDark ? 'dark' : 'light';
                this.theme = 'dark';
            }
            document.documentElement.setAttribute('data-theme', this.theme);
        }
    },
});


